import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'

// Assume these icons are imported from an icon library
import { Building2, Kanban, Store, UserCheck } from 'lucide-react'
import { useSidebar } from '../context/SidebarContext'
import { ChevronDownIcon, HorizontaLDots } from '../icons'
import SidebarWidget from './SidebarWidget'

type NavItem = {
    name: string
    icon: React.ReactNode
    path?: string
    subItems?: { icon?: React.ReactNode; name: string; path: string; pro?: boolean; new?: boolean }[]
}

const navItems: NavItem[] = [
    {
        icon: <Store className='w-5 h-5' />,
        name: 'Início',
        path: '/'
    },
    {
        icon: <Kanban className='w-5 h-5' />,
        name: 'Negócios',
        path: '/negocio'
    }
    // {
    //     icon: <Tag className='w-5 h-5' />,
    //     name: 'Etiquetas',
    //     path: '/etiqueta'
    // },
    // {
    //     icon: <ShoppingCart className='w-5 h-5' />,
    //     name: 'Produtos',
    //     path: '/produto'
    // },
    // {
    //     icon: <SquareCheckBig className='w-5 h-5' />,
    //     name: 'Atividades',
    //     path: '/atividade'
    // }
]

const othersItems: NavItem[] = [
    {
        icon: <Building2 className='w-5 h-5' />,
        name: 'Empresas',
        path: '/empresa'
    },
    {
        icon: <UserCheck className='w-5 h-5' />,
        name: 'Profissionais',
        path: '/profissional'
    }
    // {
    //     icon: <LogIn className='w-5 h-5' />,
    //     name: 'Portal do Cliente',
    //     subItems: [
    //         { name: 'Entregas', icon: <Truck className='w-4 h-4' />, path: '/portal/entrega' },
    //         { name: 'Pedidos e Faturas', icon: <FileCheck2 className='w-4 h-4' />, path: '/portal/pedido' },
    //         { name: 'Suporte', icon: <ConciergeBell className='w-4 h-4' />, path: '/portal/suporte' },
    //         { name: 'Atualizações', icon: <RefreshCw className='w-4 h-4' />, path: '/portal/atualizacao' }
    //     ]
    // }
    // {
    //     icon: <Settings className='w-5 h-5' />,
    //     name: 'Configurações',
    //     subItems: [
    //         { name: 'Personalização', path: '/portal/entrega' },
    //         { name: 'Usuários e Permissões', path: '/portal/pedido' },
    //         { name: 'Integrações', path: '/portal/suporte' }
    //     ]
    // },
    // {
    //     icon: <MessageCircleMore className='w-5 h-5' />,
    //     name: 'Ajuda e Suporte',
    //     subItems: [
    //         { name: 'Base de Conhecimento', path: '/portal/entrega' },
    //         { name: 'Suporte Técnico', path: '/portal/pedido' }
    //     ]
    // },
    // {
    //     icon: <CalendarCheck2 className='w-5 h-5' />,
    //     name: 'Calendário',
    //     path: '/calendar'
    // },
    // {
    //     name: 'Forms',
    //     icon: <ListIcon />,
    //     subItems: [{ name: 'Form Elements', path: '/form-elements', pro: false }]
    // },
    // {
    //     name: 'Tables',
    //     icon: <TableIcon />,
    //     subItems: [{ name: 'Basic Tables', path: '/basic-tables', pro: false }]
    // },
    // {
    //     name: 'Pages',
    //     icon: <PageIcon />,
    //     subItems: [
    //         { name: '404 Error', path: '/404', pro: false },
    //         { name: 'Blank Page', path: '/blank', pro: false }
    //     ]
    // },
    // {
    //     icon: <PieChartIcon />,
    //     name: 'Charts',
    //     subItems: [
    //         { name: 'Line Chart', path: '/line-chart', pro: false },
    //         { name: 'Bar Chart', path: '/bar-chart', pro: false }
    //     ]
    // },
    // {
    //     icon: <BoxCubeIcon />,
    //     name: 'UI Elements',
    //     subItems: [
    //         { name: 'Alerts', path: '/alerts', pro: false },
    //         { name: 'Avatar', path: '/avatars', pro: false },
    //         { name: 'Badge', path: '/badges', pro: false },
    //         { name: 'Buttons', path: '/buttons', pro: false },
    //         { name: 'Images', path: '/images', pro: false },
    //         { name: 'Videos', path: '/videos', pro: false }
    //     ]
    // },
    // {
    //     icon: <PlugInIcon />,
    //     name: 'Authentication',
    //     subItems: [
    //         { name: 'Sign In', path: '/login', pro: false },
    //         { name: 'Sign Up', path: '/signup', pro: false }
    //     ]
    // }
]

const AppSidebar: React.FC = () => {
    const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar()
    const location = useLocation()

    const [openSubmenu, setOpenSubmenu] = useState<{
        type: 'main' | 'outros'
        index: number
    } | null>(null)
    const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({})
    const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const isActive = useCallback(
        (path: string) => {
            if (path === '/' && location.pathname !== '/') {
                return false // Garante que '/' só é ativo na raiz
            }

            let currentPath = location.pathname

            // Função para normalizar a rota, tratando exceções com rotas dinâmicas
            const normalizeRoute = (route: string) => {
                // Divide a rota em segmentos, removendo vazios
                const segments = route.split('/').filter(Boolean)
                // Se o primeiro segmento for uma das exceções, retorna 'atividade'
                if (['tarefa', 'evento', 'chamada'].includes(segments[0])) {
                    return ['atividade', ...segments.slice(1)]
                }
                return segments
            }

            const currentPathSegments = normalizeRoute(currentPath)
            const targetPathSegments = normalizeRoute(path)

            return targetPathSegments.every((segment, index) => {
                // Verifica se os segmentos são iguais ou se o atual é numérico (para IDs dinâmicos)
                return segment === currentPathSegments[index] || /^\d+$/.test(currentPathSegments[index])
            })
        },
        [location.pathname]
    )

    useEffect(() => {
        let submenuMatched = false
        ;['main', 'outros'].forEach(menuType => {
            const items = menuType === 'main' ? navItems : othersItems
            items.forEach((nav, index) => {
                if (nav.subItems) {
                    nav.subItems.forEach(subItem => {
                        if (isActive(subItem.path)) {
                            setOpenSubmenu({
                                type: menuType as 'main' | 'outros',
                                index
                            })
                            submenuMatched = true
                        }
                    })
                }
            })
        })

        if (!submenuMatched) {
            setOpenSubmenu(null)
        }
    }, [location, isActive])

    useEffect(() => {
        if (openSubmenu !== null) {
            const key = `${openSubmenu.type}-${openSubmenu.index}`
            if (subMenuRefs.current[key]) {
                setSubMenuHeight(prevHeights => ({
                    ...prevHeights,
                    [key]: subMenuRefs.current[key]?.scrollHeight || 0
                }))
            }
        }
    }, [openSubmenu])

    const handleSubmenuToggle = (index: number, menuType: 'main' | 'outros') => {
        setOpenSubmenu(prevOpenSubmenu => {
            if (prevOpenSubmenu && prevOpenSubmenu.type === menuType && prevOpenSubmenu.index === index) {
                return null
            }
            return { type: menuType, index }
        })
    }

    const renderMenuItems = (items: NavItem[], menuType: 'main' | 'outros') => (
        <ul className='flex flex-col gap-2'>
            {items.map((nav, index) => (
                <li key={nav.name}>
                    {nav.subItems ? (
                        <button
                            onClick={() => handleSubmenuToggle(index, menuType)}
                            className={`menu-item group py-3 ${
                                openSubmenu?.type === menuType && openSubmenu?.index === index
                                    ? 'menu-item-active'
                                    : 'menu-item-inactive'
                            } cursor-pointer ${!isExpanded && !isHovered ? 'lg:justify-center' : 'lg:justify-start'}`}
                        >
                            <span
                                className={`${
                                    openSubmenu?.type === menuType && openSubmenu?.index === index
                                        ? 'menu-item-icon-active'
                                        : 'menu-item-icon-inactive'
                                }`}
                            >
                                {nav.icon}
                            </span>
                            {(isExpanded || isHovered || isMobileOpen) && (
                                <span className='menu-item-text'>{nav.name}</span>
                            )}
                            {(isExpanded || isHovered || isMobileOpen) && (
                                <ChevronDownIcon
                                    className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                                        openSubmenu?.type === menuType && openSubmenu?.index === index
                                            ? 'rotate-180 text-brand-500'
                                            : ''
                                    }`}
                                />
                            )}
                        </button>
                    ) : (
                        nav.path && (
                            <Link
                                to={nav.path}
                                className={`menu-item group py-3 ${
                                    isActive(nav.path) ? 'menu-item-active' : 'menu-item-inactive'
                                }`}
                            >
                                <span
                                    className={`${
                                        isActive(nav.path) ? 'menu-item-icon-active' : 'menu-item-icon-inactive'
                                    }`}
                                >
                                    {nav.icon}
                                </span>
                                {(isExpanded || isHovered || isMobileOpen) && (
                                    <span className='menu-item-text'>{nav.name}</span>
                                )}
                            </Link>
                        )
                    )}
                    {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
                        <div
                            ref={el => {
                                subMenuRefs.current[`${menuType}-${index}`] = el
                            }}
                            className='overflow-hidden transition-all duration-300'
                            style={{
                                height:
                                    openSubmenu?.type === menuType && openSubmenu?.index === index
                                        ? `${subMenuHeight[`${menuType}-${index}`]}px`
                                        : '0px'
                            }}
                        >
                            <ul className='mt-2 space-y-1 ml-9'>
                                {nav.subItems.map(subItem => (
                                    <li key={subItem.name}>
                                        <Link
                                            to={subItem.path}
                                            className={`menu-dropdown-item ${
                                                isActive(subItem.path)
                                                    ? 'menu-dropdown-item-active'
                                                    : 'menu-dropdown-item-inactive'
                                            }`}
                                        >
                                            {subItem.icon && (
                                                <span
                                                    className={`${
                                                        isActive(subItem.path)
                                                            ? 'menu-dropdown-item-icon-active'
                                                            : 'menu-dropdown-item-icon-inactive'
                                                    }`}
                                                >
                                                    {subItem.icon}
                                                </span>
                                            )}
                                            {subItem.name}
                                            <span className='flex items-center gap-1 ml-auto'>
                                                {subItem.new && (
                                                    <span
                                                        className={`ml-auto ${
                                                            isActive(subItem.path)
                                                                ? 'menu-dropdown-badge-active'
                                                                : 'menu-dropdown-badge-inactive'
                                                        } menu-dropdown-badge`}
                                                    >
                                                        new
                                                    </span>
                                                )}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </li>
            ))}
        </ul>
    )

    return (
        <aside
            className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-40 border-r border-gray-200 
        ${isExpanded || isMobileOpen ? 'w-[240px]' : isHovered ? 'w-[240px]' : 'w-[90px]'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
            onMouseEnter={() => !isExpanded && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className={`py-8 flex ${!isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'}`}>
                <Link to='/'>
                    {isExpanded || isHovered || isMobileOpen ? (
                        <>
                            <img className='dark:hidden' src='/images/logo/logo-light.png' alt='Logo' width={110} />
                            <img
                                className='hidden dark:block'
                                src='/images/logo/logo-dark.png'
                                alt='Logo'
                                width={110}
                            />
                        </>
                    ) : (
                        <img src='/images/logo/logo-icon.svg' alt='Logo' width={32} height={32} />
                    )}
                </Link>
            </div>
            <div className='flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar'>
                <nav className='mb-6'>
                    <div className='flex flex-col gap-4'>
                        <div>
                            <h2
                                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                                    !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
                                }`}
                            >
                                {isExpanded || isHovered || isMobileOpen ? 'Principal' : <HorizontaLDots />}
                            </h2>
                            {renderMenuItems(navItems, 'main')}
                        </div>

                        <div className=''>
                            <h2
                                className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                                    !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
                                }`}
                            >
                                {isExpanded || isHovered || isMobileOpen ? 'outros' : <HorizontaLDots />}
                            </h2>
                            {renderMenuItems(othersItems, 'outros')}
                        </div>
                    </div>
                </nav>
                {isExpanded || isHovered || isMobileOpen ? <SidebarWidget /> : null}
            </div>
        </aside>
    )
}

export default AppSidebar
